<script lang="js">
import axios from "axios";
import { mapGetters } from "vuex";
import Swal from "sweetalert2";
import avatarImage from "@/assets/img/avatar.jpg";
import { canCompleteOutstandingOLevel } from "@/utils/applicationOLevelUtils";

const O_LEVEL_RESULT_STATUS = {
  PROVIDED: "completed",
  AWAITING: "awaiting_result",
};

export default {
  name: "Application Form",
  data() {
    return {
      O_LEVEL_RESULT_STATUS,
      logger: null,
      profileImage: avatarImage,
      profileImageFile: null,
      numSittings: 1,
      sittings: [{ examType: "", examYear: "", examNumber: "" }],
      examYears: this.getExamYears(),
      kinRelationship: "",
      subjects: [
        { name: "English", grade: "", sitting: "" },
        { name: "Mathematics", grade: "", sitting: "" }
      ],
      subjectOptions: ["Mathematics", "English", "Biology", "Chemistry", "Physics",
        "Government", "Economics", "Commerce", "Literature", "Geography",
        "Further Mathematics", "Agricultural Science"
      ],
      grades: ["A1", "B2", "B3", "C4", "C5", "C6", "D7", "E8", "F9"],
      countries: [],
      states: [],
      lgas: [],
      selectedNationality: "",
      selectedState: "",
      selectedLGA: "",
      selectedPOB: "",
      oLevelResultStatus: O_LEVEL_RESULT_STATUS.PROVIDED,
      formData: {
        gender: "",
        dob: "",
        permanentAddress: "",
        maritalStatus: "",
        parentGuardian: {
          name: "",
          address: "",
          phone: ""
        },
        nextOfKin: {
          name: "",
          relationship: "",
          phone: ""
        },
        academicBackground: {
          primary: {
            name: "",
            startDate: "",
            endDate: ""
          },
          secondary: {
            name: "",
            startDate: "",
            endDate: ""
          }
        },
        isSubmitting: false,
        formErrors: [],
        touched: false, // Track if form submission was attempted
      },
      // File upload data
      files: {
        oLevelResults: {}, // { sitting1: File, sitting2: File }
        jambResult: null,
        birthCertificate: null,
      },
      uploadStatus: {
        profileImage: { uploaded: false, uploading: false, url: null },
        jambResult: { uploaded: false, uploading: false, url: null },
        birthCertificate: { uploaded: false, uploading: false, url: null },
        oLevelResults: {},
      },
      totalSize: 0,
      MAX_TOTAL_SIZE: 5 * 1024 * 1024, // 5MB
      fileErrors: {},
      isHydratingForm: false,
      isSaving: false,
      draftSaved: false,
      draftSaveError: "",
    }
  },
  created() {
    // Create a logger instance for this component
    this.logger = this.$createLogger('ApplicationForm');
  },
  async mounted() {
    await Promise.allSettled([this.fetchCountries(), this.fetchStates()]);

    // Initialize O-Level upload status for existing sittings
    for (let i = 1; i <= this.sittings.length; i++) {
      this.uploadStatus.oLevelResults[`sitting${i}`] = {
        uploaded: false,
        uploading: false,
        url: null,
      };
    }

    await this.initializeFormData();
  },
  beforeUnmount() {
    if (this._draftSaveTimer) {
      clearTimeout(this._draftSaveTimer);
    }
  },
  computed: {
    ...mapGetters(["getUser", "getApplication", "getStudent"]),
    user() {
      return this.getUser || {};
    },
    userData() {
      const application = this.getApplication || {};
      const student = this.getStudent || {};
      const applicationOffering =
        application.programOfferingId &&
        typeof application.programOfferingId === "object"
          ? application.programOfferingId
          : null;
      const studentOffering =
        student.currentEnrollmentId?.programOfferingId ||
        student.initialProgramOfferingId ||
        null;
      const applicationEntrySession =
        application.entrySessionId &&
        typeof application.entrySessionId === "object"
          ? application.entrySessionId
          : null;
      return {
        ...application,
        ...(this.user || {}),
        firstname: this.user?.firstName,
        othername: this.user?.otherName,
        lastname: this.user?.lastName,
        program: applicationOffering || studentOffering,
        entrySession: applicationEntrySession || student.entrySessionId,
      };
    },
    programmeTypeLabel() {
      const offering = this.userData.program;
      const snapshot = this.getApplication?.submissionSnapshot || {};
      const type =
        offering?.structure?.type?.description ||
        offering?.structure?.type?.type ||
        snapshot.programType;
      const mode =
        offering?.structure?.mode?.description ||
        offering?.structure?.mode?.mode ||
        snapshot.programMode;
      return [type, mode].filter(Boolean).join(" · ") || "N/A";
    },
    programmeSchoolName() {
      return this.userData.program?.department?.school?.name || "N/A";
    },
    programmeDepartmentName() {
      return (
        this.userData.program?.department?.name ||
        this.getApplication?.submissionSnapshot?.departmentName ||
        "N/A"
      );
    },
    programmeName() {
      return (
        this.userData.program?.program?.name ||
        this.getApplication?.submissionSnapshot?.programName ||
        "N/A"
      );
    },
    entrySessionLabel() {
      return (
        this.userData.entrySession?.sessionYear ||
        this.getApplication?.submissionSnapshot?.sessionYear ||
        "N/A"
      );
    },
    isOLevelCompletionMode() {
      return this.$route?.name === "applicationOLevelCompletion";
    },
    formHeading() {
      return this.isOLevelCompletionMode
        ? "Complete Application Form"
        : "Application Form";
    },
    formDescription() {
      return this.isOLevelCompletionMode
        ? "Complete your pending Post Secondary School details, O'Level result, and O'Level uploads. Other application fields are read-only here."
        : "Please fill with accurate information, you can combine at most two diiffrent result.";
    },
    showAwaitingResultToggle() {
      return !this.isOLevelCompletionMode;
    },
    submitButtonText() {
      if (this.formData.isSubmitting) {
        return this.isOLevelCompletionMode
          ? "Completing..."
          : "Submitting...";
      }

      return this.isOLevelCompletionMode
        ? "Complete Application Form"
        : "Submit";
    },
    sittingNumbers() {
      return Array.from({ length: this.sittings.length }, (_, i) => i + 1);
    },
    allFilesUploaded() {
      const profileUploaded = this.uploadStatus.profileImage.uploaded;
      const jambUploaded = this.uploadStatus.jambResult.uploaded;
      const birthCertUploaded = this.uploadStatus.birthCertificate.uploaded;

      const oLevelUploaded = this.isAwaitingOLevelResult || this.sittingNumbers.every(
        (num) => this.uploadStatus.oLevelResults[`sitting${num}`]?.uploaded
      );

      return (
        profileUploaded && jambUploaded && birthCertUploaded && oLevelUploaded
      );
    },
    isAwaitingOLevelResult() {
      return (
        !this.isOLevelCompletionMode &&
        this.oLevelResultStatus === O_LEVEL_RESULT_STATUS.AWAITING
      );
    },
    allOLevelResultsUploaded() {
      if (this.isAwaitingOLevelResult) {
        return true;
      }

      return this.sittingNumbers.every(
        (num) => this.uploadStatus.oLevelResults[`sitting${num}`]?.uploaded
      );
    },
    totalSizeFormatted() {
      return (this.totalSize / 1024 / 1024).toFixed(2);
    },
    remainingSizeFormatted() {
      return ((this.MAX_TOTAL_SIZE - this.totalSize) / 1024 / 1024).toFixed(2);
    },
    // Field validation helpers
    showValidation() {
      return this.formData.touched;
    },
    // Maximum date of birth (15 years ago from today)
    maxDateOfBirth() {
      const today = new Date();
      const maxDate = new Date(today.getFullYear() - 15, today.getMonth(), today.getDate());
      return maxDate.toISOString().split('T')[0];
    },
    // Primary school start date (minimum 5 years after DOB)
    minPrimaryStartDate() {
      if (!this.formData.dob) return null;
      const dob = new Date(this.formData.dob);
      const minDate = new Date(dob.getFullYear() + 5, dob.getMonth(), dob.getDate());
      return minDate.toISOString().split('T')[0];
    },
    // Primary school start date (cannot be after today)
    maxPrimaryStartDate() {
      const today = new Date();
      return today.toISOString().split('T')[0];
    },
    // Primary school end date (must be after start date)
    minPrimaryEndDate() {
      if (!this.formData.academicBackground.primary.startDate) return null;
      const startDate = new Date(this.formData.academicBackground.primary.startDate);
      // Add 1 year minimum duration
      const minDate = new Date(startDate.getFullYear() + 1, startDate.getMonth(), startDate.getDate());
      return minDate.toISOString().split('T')[0];
    },
    // Primary school end date (cannot be after today)
    maxPrimaryEndDate() {
      const today = new Date();
      return today.toISOString().split('T')[0];
    },
    // Secondary school start date (must be after primary end date)
    minSecondaryStartDate() {
      if (!this.formData.academicBackground.primary.endDate) return null;
      const primaryEndDate = new Date(this.formData.academicBackground.primary.endDate);
      // Can start same year or later
      return primaryEndDate.toISOString().split('T')[0];
    },
    // Secondary school start date (cannot be after today)
    maxSecondaryStartDate() {
      const today = new Date();
      return today.toISOString().split('T')[0];
    },
    // Secondary school end date (must be after start date)
    minSecondaryEndDate() {
      if (!this.formData.academicBackground.secondary.startDate) return null;
      const startDate = new Date(this.formData.academicBackground.secondary.startDate);
      // Add 1 year minimum duration
      const minDate = new Date(startDate.getFullYear() + 1, startDate.getMonth(), startDate.getDate());
      return minDate.toISOString().split('T')[0];
    },
    // Secondary school end date (cannot be after today)
    maxSecondaryEndDate() {
      const today = new Date();
      return today.toISOString().split('T')[0];
    },
  },
  methods: {
    getExamYears() {
      const currentYear = new Date().getFullYear();
      const startYear = 1980; // Adjust as needed
      return Array.from({ length: currentYear - startYear + 1 }, (_, i) => currentYear - i);
    },
    async initializeFormData() {
      const storeBiodata = this.getApplication;

      if (storeBiodata) {
        await this.rehydrateFromBiodata(storeBiodata);
      }

      if (!this.userData.applicationNumber) {
        return;
      }

      await this.loadUploadStatus();
      await this.fetchAndRehydrate();
    },
    canAccessOLevelCompletion(biodata) {
      return canCompleteOutstandingOLevel(biodata);
    },
    getSubmitOLevelResultStatus() {
      return this.isOLevelCompletionMode
        ? O_LEVEL_RESULT_STATUS.PROVIDED
        : this.oLevelResultStatus;
    },
    redirectToResources() {
      this.$router.push({ name: "resources" });
    },
    getExaminationsPayload() {
      if (this.isAwaitingOLevelResult) {
        return [];
      }

      return this.sittings.map((sitting, index) => ({
        examType: sitting.examType,
        examYear: sitting.examYear,
        examNumber: sitting.examNumber,
        subjects: this.subjects
          .filter((subject) => subject.sitting === index + 1)
          .map((subject) => ({
            name: subject.name,
            grade: subject.grade,
          })),
      }));
    },
    getOLevelResultsPayload() {
      if (this.isAwaitingOLevelResult) {
        return {};
      }

      const oLevelResults = {};
      Object.keys(this.uploadStatus.oLevelResults).forEach((key) => {
        oLevelResults[key] = this.uploadStatus.oLevelResults[key].url ?? null;
      });
      return oLevelResults;
    },
    resetUploadedFileState(fileType, sittingNumber = null) {
      if (fileType === "olevel") {
        this.uploadStatus.oLevelResults[`sitting${sittingNumber}`] = {
          uploaded: false,
          uploading: false,
          url: null,
        };
        this.files.oLevelResults[`sitting${sittingNumber}`] = null;
        this.fileErrors[`olevel${sittingNumber}`] = null;
        return;
      }

      if (fileType === "profileImage") {
        this.uploadStatus.profileImage = {
          uploaded: false,
          uploading: false,
          url: null,
        };
        this.profileImageFile = null;
        this.profileImage = avatarImage;
        this.fileErrors.profileImage = null;
        return;
      }

      this.uploadStatus[fileType] = {
        uploaded: false,
        uploading: false,
        url: null,
      };
      this.files[fileType] = null;
      this.fileErrors[fileType] = null;
    },
    clearOLevelValidationState() {
      this.sittingNumbers.forEach((num) => {
        this.fileErrors[`olevel${num}`] = null;
      });
    },
    openFilePicker() {
      if (this.isOLevelCompletionMode) {
        return;
      }

      this.$refs.fileInput.click();
    },
    async handleImageUpload(event) {
      if (this.isOLevelCompletionMode) {
        return;
      }

      const file = event.target.files[0];
      if (file) {
        // Validate file type
        const validTypes = ["image/jpeg", "image/jpg", "image/png"];
        if (!validTypes.includes(file.type)) {
          this.fileErrors.profileImage = "Please select a JPEG or PNG image";
          return;
        }

        // Validate file size (before compression)
        if (file.size > 2 * 1024 * 1024) {
          this.fileErrors.profileImage = "Image must be less than 2MB";
          return;
        }

        this.profileImageFile = file;
        this.profileImage = URL.createObjectURL(file);
        this.fileErrors.profileImage = null;

        // Upload to Digital Ocean Spaces
        await this.uploadFileToSpaces("profileImage", file);
      }
    },
    addSitting() {
      if (this.isAwaitingOLevelResult) {
        return;
      }

      if (this.sittings.length < 2) { // Limit to 2 sittings
        this.sittings.push({ examType: "", examYear: "", examNumber: "" });
      }
    },
    removeSitting(index) {
      if (this.isAwaitingOLevelResult) {
        return;
      }

      if (this.sittings.length > 1) {
        this.sittings.splice(index, 1);
        // Update subjects' sitting values if the second sitting is removed
        this.subjects.forEach((subject) => {
          if (subject.sitting === 2) {
            subject.sitting = 1; // Default all to sitting 1
          }
        });
      }
    },
    addSubject() {
      if (this.isAwaitingOLevelResult) {
        return;
      }

      if (this.subjects.length < 11) { // Max 11 subjects
        this.subjects.push({ name: "", grade: "", sitting: "" });
      }
    },
    removeSubject(index) {
      if (this.isAwaitingOLevelResult) {
        return;
      }

      if (this.subjects.length > 1) {
        this.subjects.splice(index, 1);
      }
    },
    availableSubjects(index) {
      const selectedSubjects = this.subjects.map(subject => subject.name);
      return this.subjectOptions.filter(subject =>
        !selectedSubjects.includes(subject) || subject === this.subjects[index].name
      );
    },
    async fetchCountries() {
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API_BASE_URL}/applications/reference/countries`,
          {
            withCredentials: false,
            headers: {},
          }
        );

        const countries = response.data?.data?.countries || [];

        this.countries = countries
          .map(country => ({
            name: country.name,
            cca2: country.cca2
          }))
          .sort((a, b) => a.name.localeCompare(b.name)); // Sort alphabetically
      } catch (error) {
        this.logger.error("Error fetching countries:", error);
      }
    },
    async fetchStates() {
      try {
        const response = await fetch("https://nga-states-lga.onrender.com/fetch", {
          method: "GET",
          mode: "cors",
          credentials: "omit",
        });

        if (!response.ok) {
          throw new Error("Failed to fetch states");
        }

        this.states = await response.json();
      } catch (error) {
        this.logger.error("Error fetching states:", error);
      }
    },
    async fetchLGAs() {
      if (!this.selectedState) return;
      try {
        const response = await fetch(`https://nga-states-lga.onrender.com/?state=${encodeURIComponent(this.selectedState)}`, {
          method: "GET",
          mode: "cors",
          credentials: "omit",
        });

        if (!response.ok) {
          throw new Error("Failed to fetch LGAs");
        }

        this.lgas = await response.json();
      } catch (error) {
        this.logger.error("Error fetching LGAs:", error);
      }
    },
    validateForm() {
      // Reset all errors first
      this.formData.formErrors = [];

      let isValid = true;

      // Validate all fields and mark them as touched for immediate feedback
      // The actual validation will be shown via computed properties and :class bindings

      // Scroll to first error if validation fails
      this.$nextTick(() => {
        const firstInvalid = document.querySelector('.is-invalid');
        if (firstInvalid) {
          firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      });

      // Late O-Level completion is an independent operation. Do not block it
      // because of an unrelated historical application field or document.
      if (this.isOLevelCompletionMode) {
        for (const sitting of this.sittings) {
          if (!sitting.examType || !sitting.examYear || !sitting.examNumber) {
            isValid = false;
          }
        }

        for (const subject of this.subjects) {
          if (!subject.name || !subject.grade || !subject.sitting) {
            isValid = false;
          }
        }

        for (const num of this.sittingNumbers) {
          if (!this.uploadStatus.oLevelResults[`sitting${num}`]?.uploaded) {
            isValid = false;
          }
        }

        return isValid;
      }

      // Check if all required fields are filled
      if (!this.uploadStatus.profileImage.uploaded) isValid = false;
      if (!this.formData.gender) isValid = false;
      if (!this.formData.dob) isValid = false;
      if (!this.formData.permanentAddress) isValid = false;
      if (!this.selectedNationality) isValid = false;
      if (this.selectedNationality === "Nigeria" && !this.selectedState) isValid = false;
      if (this.selectedNationality === "Nigeria" && !this.selectedLGA) isValid = false;
      if (!this.selectedPOB) isValid = false;
      if (!this.formData.maritalStatus) isValid = false;
      if (!this.formData.parentGuardian.name) isValid = false;
      if (!this.formData.parentGuardian.address) isValid = false;
      if (!this.formData.parentGuardian.phone) isValid = false;
      if (!this.formData.nextOfKin.name) isValid = false;
      if (!this.kinRelationship) isValid = false;
      if (!this.formData.nextOfKin.phone) isValid = false;
      if (!this.formData.academicBackground.primary.name) isValid = false;
      if (!this.formData.academicBackground.primary.startDate) isValid = false;
      if (!this.formData.academicBackground.primary.endDate) isValid = false;
      if (!this.formData.academicBackground.secondary.name) isValid = false;
      if (!this.formData.academicBackground.secondary.startDate) isValid = false;
      if (!this.formData.academicBackground.secondary.endDate) isValid = false;

      if (!this.isAwaitingOLevelResult) {
        // Check exam sittings
        for (let i = 0; i < this.sittings.length; i++) {
          const sitting = this.sittings[i];
          if (!sitting.examType || !sitting.examYear || !sitting.examNumber) isValid = false;
        }

        // Check subjects
        for (let i = 0; i < this.subjects.length; i++) {
          const subject = this.subjects[i];
          if (!subject.name || !subject.grade || !subject.sitting) isValid = false;
        }
      }

      // Check file uploads
      if (!this.uploadStatus.profileImage.uploaded) isValid = false;
      if (!this.uploadStatus.jambResult.uploaded) isValid = false;
      if (!this.uploadStatus.birthCertificate.uploaded) isValid = false;

      for (let num of this.sittingNumbers) {
        if (
          !this.isAwaitingOLevelResult &&
          !this.uploadStatus.oLevelResults[`sitting${num}`]?.uploaded
        ) {
          isValid = false;
        }
      }

      return isValid;
    },
    async submitForm() {
      // Mark form as touched to show validation
      this.formData.touched = true;

      // Validate form
      const isValid = this.validateForm();
      if (!isValid) {
        Swal.fire({
          icon: 'warning',
          title: 'Incomplete Form',
          text: 'Please fill in all required fields correctly.',
          confirmButtonText: 'OK'
        });
        return;
      }

      this.formData.isSubmitting = true;

      try {

        // Authentication handled automatically via httpOnly cookies

        // Prepare request data directly matching the server's expected structure
        const requestData = {
          dob: this.formData.dob,
          gender: this.formData.gender,
          permanentAddress: this.formData.permanentAddress,
          nationality: this.selectedNationality,
          stateOfOrigin: this.selectedState,
          lga: this.selectedLGA,
          placeOfBirth: this.selectedPOB,
          maritalStatus: this.formData.maritalStatus,
          guardian: this.formData.parentGuardian,
          nextOfKin: {
            name: this.formData.nextOfKin.name,
            relationship: this.kinRelationship,
            phone: this.formData.nextOfKin.phone
          },
          oLevelResultStatus: this.getSubmitOLevelResultStatus(),
          academicBackground: {
            primary: {
              name: this.formData.academicBackground.primary.name,
              startDate: this.formData.academicBackground.primary.startDate,
              endDate: this.formData.academicBackground.primary.endDate
            },
            secondary: {
              name: this.formData.academicBackground.secondary.name,
              startDate: this.formData.academicBackground.secondary.startDate,
              endDate: this.formData.academicBackground.secondary.endDate
            }
          },
          examinations: this.getExaminationsPayload(),
        };

        // Send the data to the server
        const response = await axios.post(
          `${process.env.VUE_APP_API_BASE_URL}/applications/me/${this.isOLevelCompletionMode ? "o-level" : "submit"}`,
          requestData,
          {
            headers: {
              'Content-Type': 'application/json'
            }
          }
        );

        if (response.data && response.data.success) {
          // Show success message
          Swal.fire({
            title: 'Success!',
            text: this.isOLevelCompletionMode
              ? "Your O'Level result has been completed successfully."
              : 'Your application form has been submitted successfully.',
            icon: 'success',
            confirmButtonText: 'OK'
          }).then(() => {
            // Refresh user data
            this.fetchUserData();
            if (this.isOLevelCompletionMode) {
              this.$urlHelper.goToPortal('/resources');
            } else {
              this.$urlHelper.goToPortal('/dashboard');
            }
          });
        } else {
          throw new Error(response.data.message || 'Failed to submit application form');
        }
      } catch (error) {
        this.logger.error('Error submitting application form:', error);
        Swal.fire({
          title: 'Error!',
          text: error.message || 'Failed to submit application form. Please try again.',
          icon: 'error',
          confirmButtonText: 'OK'
        });
      } finally {
        this.formData.isSubmitting = false;
      }
    },
    async fetchUserData() {
      try {
        await this.$store.dispatch("refreshSession");
      } catch (error) {
        this.logger.error("Error fetching user data:", error);
      }
    },
    // File upload methods
    async uploadFileToSpaces(fileType, file, sittingNumber = null) {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("fileType", fileType === "olevel" ? "olevel" : fileType);

      if (sittingNumber) {
        formData.append("sittingNumber", sittingNumber);
      }

      // Update status
      if (fileType === "olevel") {
        if (!this.uploadStatus.oLevelResults[`sitting${sittingNumber}`]) {
          this.uploadStatus.oLevelResults[`sitting${sittingNumber}`] = {
            uploaded: false,
            uploading: false,
            url: null,
          };
        }
        this.uploadStatus.oLevelResults[`sitting${sittingNumber}`].uploading = true;
      } else {
        this.uploadStatus[fileType].uploading = true;
      }

      try {
        const response = await axios.post(
          `${process.env.VUE_APP_API_BASE_URL}/file-upload/documents`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );

        if (response.data.success) {
          if (fileType === "olevel") {
            this.uploadStatus.oLevelResults[`sitting${sittingNumber}`].uploaded = true;
            this.uploadStatus.oLevelResults[`sitting${sittingNumber}`].uploading = false;
            this.uploadStatus.oLevelResults[`sitting${sittingNumber}`].url = response.data.data.contentUrl;
            this.uploadStatus.oLevelResults[`sitting${sittingNumber}`].id = response.data.data.document._id;
            // Persist olevel URL to draft
            await this.saveDraftFiles();
          } else {
            this.uploadStatus[fileType].uploaded = true;
            this.uploadStatus[fileType].uploading = false;
            this.uploadStatus[fileType].url = response.data.data.contentUrl;
            this.uploadStatus[fileType].id = response.data.data.document._id;
            // Persist file URL to draft
            await this.saveDraftFiles();
          }

          await this.loadUploadStatus();

          Swal.fire({
            icon: "success",
            title: "Uploaded!",
            text: `${fileType === "olevel" ? `Sitting ${sittingNumber}` : fileType} uploaded successfully`,
            timer: 2000,
            showConfirmButton: false,
          });
        }
      } catch (error) {
        const errorMessage = error.response?.data?.message || "Failed to upload file";

        if (fileType === "olevel") {
          this.uploadStatus.oLevelResults[`sitting${sittingNumber}`].uploading = false;
          this.fileErrors[`olevel${sittingNumber}`] = errorMessage;
        } else {
          this.uploadStatus[fileType].uploading = false;
          this.fileErrors[fileType] = errorMessage;
        }

        Swal.fire({
          icon: "error",
          title: "Upload Failed",
          text: errorMessage,
        });
      }
    },
    handlePdfSelect(fileType, event, sittingNumber = null) {
      if (this.isOLevelCompletionMode && fileType !== "olevel") {
        return;
      }

      const file = event.target.files[0];
      if (!file) return;

      // Validate file type
      if (file.type !== "application/pdf") {
        const key = sittingNumber ? `${fileType}${sittingNumber}` : fileType;
        this.fileErrors[key] = "Please select a PDF file";
        return;
      }

      // Validate file size
      if (file.size > 2 * 1024 * 1024) {
        const key = sittingNumber ? `${fileType}${sittingNumber}` : fileType;
        this.fileErrors[key] = "PDF must be less than 2MB";
        return;
      }

      if (fileType === "olevel" && sittingNumber) {
        this.files.oLevelResults[`sitting${sittingNumber}`] = file;
        this.fileErrors[`olevel${sittingNumber}`] = null;
        this.uploadFileToSpaces("olevel", file, sittingNumber);
      } else {
        this.files[fileType] = file;
        this.fileErrors[fileType] = null;
        this.uploadFileToSpaces(fileType, file);
      }
    },
    async deleteUploadedFile(fileType, sittingNumber = null, options = {}) {
      const {
        silent = false,
        suppressError = false,
        skipReload = false,
      } = options;

      if (this.isOLevelCompletionMode && fileType !== "olevel") {
        return;
      }

      try {
        const uploadState = fileType === "olevel" ? this.uploadStatus.oLevelResults[`sitting${sittingNumber}`] : this.uploadStatus[fileType];
        if (uploadState?.id) await axios.delete(`${process.env.VUE_APP_API_BASE_URL}/file-upload/documents/${uploadState.id}`);

        // Reset status
        this.resetUploadedFileState(fileType, sittingNumber);
        await this.saveDraftFiles();

        if (!skipReload) {
          await this.loadUploadStatus();
        }

        if (!silent) {
          Swal.fire({
            icon: "success",
            title: "Deleted!",
            text: "File deleted successfully",
            timer: 2000,
            showConfirmButton: false,
          });
        }
      } catch (error) {
        if (suppressError) {
          this.resetUploadedFileState(fileType, sittingNumber);
          try {
            await this.saveDraftFiles();
            if (!skipReload) {
              await this.loadUploadStatus();
            }
          } catch (saveError) {
            this.logger.error("Error saving draft after silent delete fallback:", saveError);
          }
          return;
        }

        Swal.fire({
          icon: "error",
          title: "Delete Failed",
          text: "Failed to delete file",
        });
      }
    },
    async clearUploadedOLevelResults() {
      const sittingNumbers = [...this.sittingNumbers];

      for (const num of sittingNumbers) {
        const uploadState = this.uploadStatus.oLevelResults[`sitting${num}`];
        if (uploadState?.uploaded || uploadState?.url) {
          await this.deleteUploadedFile("olevel", num, {
            silent: true,
            suppressError: true,
            skipReload: true,
          });
        } else {
          this.resetUploadedFileState("olevel", num);
        }
      }

      this.clearOLevelValidationState();
      await this.saveDraftFiles();
      await this.loadUploadStatus();
    },
    async loadUploadStatus() {
      try {
        const response = await axios.get(`${process.env.VUE_APP_API_BASE_URL}/file-upload/documents`);

        if (response.data.success) {
          const documents = response.data.data;
          const permanent = { oLevelResults: {} };
          documents.forEach((document) => {
            const state = { url: document.contentUrl, id: document._id, size: document.size };
            if (document.documentType === "profile_image") permanent.profileImage = state;
            if (document.documentType === "jamb_result") permanent.jambResult = state;
            if (document.documentType === "birth_certificate") permanent.birthCertificate = state;
            if (document.documentType === "o_level_result") permanent.oLevelResults[`sitting${document.sittingNumber}`] = state;
          });

          // Update upload status from permanent files
          if (permanent.profileImage) {
            this.uploadStatus.profileImage.uploaded = true;
            this.uploadStatus.profileImage.url = permanent.profileImage.url;
            this.uploadStatus.profileImage.id = permanent.profileImage.id;
            const image = await axios.get(`${process.env.VUE_APP_API_BASE_URL}${permanent.profileImage.url}`, { responseType: "blob" });
            this.profileImage = URL.createObjectURL(image.data);
          }

          if (permanent.jambResult) {
            this.uploadStatus.jambResult.uploaded = true;
            Object.assign(this.uploadStatus.jambResult, permanent.jambResult);
          }

          if (permanent.birthCertificate) {
            this.uploadStatus.birthCertificate.uploaded = true;
            Object.assign(this.uploadStatus.birthCertificate, permanent.birthCertificate);
          }

          // O-Level results
          if (permanent.oLevelResults) {
            Object.keys(permanent.oLevelResults).forEach((key) => {
              const document = permanent.oLevelResults[key];
              if (!this.uploadStatus.oLevelResults[key]) {
                this.uploadStatus.oLevelResults[key] = {
                  uploaded: false,
                  uploading: false,
                  url: null,
                };
              }
              if (document) {
                this.uploadStatus.oLevelResults[key].uploaded = true;
                Object.assign(this.uploadStatus.oLevelResults[key], document);
              }
            });
          }

          this.totalSize = documents.reduce((sum, document) => sum + Number(document.size || 0), 0);
        }
      } catch (error) {
        this.logger.error("Error loading upload status:", error);
      }
    },

    // ── Draft save ────────────────────────────────────────────────────────────
    scheduleDraftSave() {
      if (this.isHydratingForm) return;
      if (this._draftSaveTimer) clearTimeout(this._draftSaveTimer);
      this._draftSaveTimer = setTimeout(() => this.saveDraft(), 1500);
    },

    async saveDraft() {
      if (this.isSaving || this.isOLevelCompletionMode || this.getApplication?.status !== "draft") return;
      this.isSaving = true;
      this.draftSaved = false;
      this.draftSaveError = "";
      try {
        await axios.patch(
          `${process.env.VUE_APP_API_BASE_URL}/applications/me/draft`,
          {
            dob: this.formData.dob || undefined,
            gender: this.formData.gender || undefined,
            permanentAddress: this.formData.permanentAddress || undefined,
            nationality: this.selectedNationality || undefined,
            stateOfOrigin: this.selectedState || undefined,
            lga: this.selectedLGA || undefined,
            placeOfBirth: this.selectedPOB || undefined,
            maritalStatus: this.formData.maritalStatus || undefined,
            guardian: this.formData.parentGuardian,
            nextOfKin: {
              name: this.formData.nextOfKin.name,
              relationship: this.kinRelationship,
              phone: this.formData.nextOfKin.phone,
            },
            academicBackground: this.formData.academicBackground,
            oLevelResultStatus: this.oLevelResultStatus,
            examinations: this.getExaminationsPayload(),
          },
          { headers: { "Content-Type": "application/json" } }
        );
        this.draftSaved = true;
        setTimeout(() => { this.draftSaved = false; }, 3000);
      } catch (error) {
        this.draftSaveError =
          error.response?.data?.message || "Unable to save your progress.";
        this.logger.error("Error saving application draft", {
          status: error.response?.status,
          message: this.draftSaveError,
        });
      } finally {
        this.isSaving = false;
      }
    },

    // Saves only file URLs (and nulls) to the draft — called after upload/delete.
    // Does not debounce — file operations need immediate persistence.
    async saveDraftFiles() {
      try {
        if (this.isOLevelCompletionMode) return;
        await axios.patch(
          `${process.env.VUE_APP_API_BASE_URL}/applications/me/draft`,
          {
            oLevelResultStatus: this.oLevelResultStatus,
          },
          { headers: { "Content-Type": "application/json" } }
        );
      } catch (error) {
        this.logger.error("Error saving draft file URLs:", error);
      }
    },

    async fetchAndRehydrate() {
      try {
        const response = await axios.get(`${process.env.VUE_APP_API_BASE_URL}/applications/me`);
        if (response.data?.data) {
          const biodata = response.data.data;

          if (
            this.isOLevelCompletionMode &&
            !this.canAccessOLevelCompletion(biodata)
          ) {
            Swal.fire({
              icon: "info",
              title: "O-Level completion not available",
              text: "This application is no longer awaiting O-Level completion.",
              confirmButtonText: "OK",
            }).then(() => {
              this.redirectToResources();
            });
            return;
          }

          await this.rehydrateFromBiodata(biodata);
        } else if (this.isOLevelCompletionMode) {
          this.redirectToResources();
        }
      } catch (error) {
        this.logger.error("Error fetching draft:", error);
      }
    },

    async rehydrateFromBiodata(biodata) {
      if (!biodata || (biodata.status !== "draft" && !this.isOLevelCompletionMode)) {
        return;
      }

      this.isHydratingForm = true;

      try {
        this.oLevelResultStatus =
          biodata.oLevelResultStatus || O_LEVEL_RESULT_STATUS.PROVIDED;

        this.formData.gender = biodata.gender || "";
        this.formData.dob = this.user?.dateOfBirth
          ? new Date(this.user.dateOfBirth).toISOString().split("T")[0]
          : "";
        this.formData.permanentAddress = biodata.permanentAddress?.line1 || "";
        this.selectedNationality = biodata.nationality || "";
        this.selectedState = biodata.stateOfOrigin || "";
        this.selectedLGA = biodata.lga || "";
        this.selectedPOB = biodata.placeOfBirth || "";
        this.formData.maritalStatus = biodata.maritalStatus || "";

        const guardian = biodata.guardian || biodata.parentGuardian || {};
        this.formData.parentGuardian.name = guardian.name || "";
        this.formData.parentGuardian.address = guardian.address || "";
        this.formData.parentGuardian.phone = guardian.phone || "";

        const nextOfKin = biodata.nextOfKin || {};
        this.formData.nextOfKin.name = nextOfKin.name || "";
        this.kinRelationship = nextOfKin.relationship || "";
        this.formData.nextOfKin.phone = nextOfKin.phone || "";

        const primary = biodata.academicBackground?.primary || {};
        const secondary = biodata.academicBackground?.secondary || {};
        this.formData.academicBackground.primary.name = primary.name || "";
        this.formData.academicBackground.primary.startDate = primary.startDate
          ? new Date(primary.startDate).toISOString().split("T")[0]
          : "";
        this.formData.academicBackground.primary.endDate = primary.endDate
          ? new Date(primary.endDate).toISOString().split("T")[0]
          : "";
        this.formData.academicBackground.secondary.name = secondary.name || "";
        this.formData.academicBackground.secondary.startDate = secondary.startDate
          ? new Date(secondary.startDate).toISOString().split("T")[0]
          : "";
        this.formData.academicBackground.secondary.endDate = secondary.endDate
          ? new Date(secondary.endDate).toISOString().split("T")[0]
          : "";

        if (Array.isArray(biodata.examinations) && biodata.examinations.length > 0) {
          this.sittings = biodata.examinations.map((exam) => ({
            examType: exam.examType || "",
            examYear: exam.examYear || "",
            examNumber: exam.examNumber || "",
          }));

          const allSubjects = [];
          biodata.examinations.forEach((exam, examIndex) => {
            if (Array.isArray(exam.subjects) && exam.subjects.length > 0) {
              exam.subjects.forEach((subject) => {
                allSubjects.push({
                  name: subject.name || "",
                  grade: subject.grade || "",
                  sitting: examIndex + 1,
                });
              });
            }
          });

          if (allSubjects.length >= 2) {
            this.subjects = allSubjects;
          }
        }

        if (this.selectedNationality === "Nigeria" && this.selectedState) {
          await this.fetchLGAs();
        }

        await this.$nextTick();
      } finally {
        this.isHydratingForm = false;
      }
    },
  },
  watch: {
    async 'userData.applicationNumber'(newValue, oldValue) {
      if (!newValue || newValue === oldValue) {
        return;
      }

      await this.initializeFormData();
    },
    async oLevelResultStatus(newValue, oldValue) {
      if (this.isHydratingForm) {
        return;
      }

      if (newValue === oldValue) {
        return;
      }

      if (newValue === O_LEVEL_RESULT_STATUS.AWAITING) {
        await this.clearUploadedOLevelResults();
      }

      this.scheduleDraftSave();
    },
    sittings: {
      handler(newVal) {
        if (this.isHydratingForm) {
          return;
        }

        // Initialize upload status for new sittings
        for (let i = 1; i <= newVal.length; i++) {
          const key = `sitting${i}`;
          if (!this.uploadStatus.oLevelResults[key]) {
            this.uploadStatus.oLevelResults[key] = {
              uploaded: false,
              uploading: false,
              url: null,
            };
          }
        }
        this.scheduleDraftSave();
      },
      deep: true,
    },
    selectedNationality(newValue) {
      if (this.isHydratingForm) {
        return;
      }

      // If nationality is null, reset state, LGA and place of birth
      if (!newValue) {
        this.selectedState = null;
        this.selectedLGA = null;
        this.selectedPOB = null;
        this.states = [];
        this.lgas = [];
      } else {
        // If Nigeria is selected, fetch states
        this.fetchStates();
      }
      this.scheduleDraftSave();
    },
    selectedState(newValue) {
      if (this.isHydratingForm) {
        return;
      }

      // If state is null, reset LGA
      if (!newValue) {
        this.selectedLGA = null;
        this.lgas = [];
      } else {
        // If a state is selected, fetch LGAs
        this.fetchLGAs();
      }
      this.scheduleDraftSave();
    },
    // Reset primary school dates if DOB changes
    'formData.dob'(newValue, oldValue) {
      if (this.isHydratingForm) {
        return;
      }

      if (newValue !== oldValue && this.formData.academicBackground.primary.startDate) {
        // Check if current start date is still valid
        const minDate = this.minPrimaryStartDate;
        if (minDate && this.formData.academicBackground.primary.startDate < minDate) {
          this.formData.academicBackground.primary.startDate = '';
          this.formData.academicBackground.primary.endDate = '';
        }
      }
      this.scheduleDraftSave();
    },
    // Reset primary end date if start date changes
    'formData.academicBackground.primary.startDate'(newValue, oldValue) {
      if (this.isHydratingForm) {
        return;
      }

      if (newValue !== oldValue && this.formData.academicBackground.primary.endDate) {
        // Check if current end date is still valid
        const minDate = this.minPrimaryEndDate;
        if (minDate && this.formData.academicBackground.primary.endDate < minDate) {
          this.formData.academicBackground.primary.endDate = '';
        }
      }
      this.scheduleDraftSave();
    },
    // Reset secondary dates if primary end date changes
    'formData.academicBackground.primary.endDate'(newValue, oldValue) {
      if (this.isHydratingForm) {
        return;
      }

      if (newValue !== oldValue && this.formData.academicBackground.secondary.startDate) {
        // Check if current secondary start date is still valid
        const minDate = this.minSecondaryStartDate;
        if (minDate && this.formData.academicBackground.secondary.startDate < minDate) {
          this.formData.academicBackground.secondary.startDate = '';
          this.formData.academicBackground.secondary.endDate = '';
        }
      }
      this.scheduleDraftSave();
    },
    // Reset secondary end date if start date changes
    'formData.academicBackground.secondary.startDate'(newValue, oldValue) {
      if (this.isHydratingForm) {
        return;
      }

      if (newValue !== oldValue && this.formData.academicBackground.secondary.endDate) {
        // Check if current end date is still valid
        const minDate = this.minSecondaryEndDate;
        if (minDate && this.formData.academicBackground.secondary.endDate < minDate) {
          this.formData.academicBackground.secondary.endDate = '';
        }
      }
      this.scheduleDraftSave();
    },
    // Additional draft-save triggers
    subjects: {
      handler() { this.scheduleDraftSave(); },
      deep: true,
    },
    kinRelationship() { this.scheduleDraftSave(); },
    selectedLGA() { this.scheduleDraftSave(); },
    selectedPOB() { this.scheduleDraftSave(); },
    'formData.gender'() { this.scheduleDraftSave(); },
    'formData.maritalStatus'() { this.scheduleDraftSave(); },
    'formData.permanentAddress'() { this.scheduleDraftSave(); },
    'formData.parentGuardian': {
      handler() { this.scheduleDraftSave(); },
      deep: true,
    },
    'formData.nextOfKin': {
      handler() { this.scheduleDraftSave(); },
      deep: true,
    },
    'formData.academicBackground.primary.name'() { this.scheduleDraftSave(); },
    'formData.academicBackground.secondary.name'() { this.scheduleDraftSave(); },
  },
};
</script>

<template>
  <div class="p-4 text-center">
    <RouterLink to="/app">
      <img src="@/assets/logo.png" class="" alt="" width="96.26" height="70" />
    </RouterLink>
    <hr />
    <h4 class="fw-bold mt-4">{{ formHeading }}</h4>

    <p>
      {{ formDescription }}
      <template v-if="!isOLevelCompletionMode">
        Fields with <span class="text-danger">*</span> are required.
      </template>
    </p>
    <form class="text-start col-md-8 mx-auto" @submit.prevent="submitForm">
      <fieldset
        :disabled="isOLevelCompletionMode"
        :class="{ 'locked-form-section': isOLevelCompletionMode }"
      >
        <!-- Personal Information -->
        <div class="d-flex justify-content-center mt-5 mb-3">
          <div
            class="title-container d-inline-flex align-items-center position-relative"
          >
            <i class="bi bi-person-circle fs-4 me-2"></i>
            <h6 class="fw-bold mb-0">Personal Information</h6>
          </div>
        </div>
        <div class="row">
          <div class="col-md-3 mb-3">
            <div
              class="image-upload-container"
              :class="{
                'border border-danger border-1':
                  !uploadStatus.profileImage.uploaded && showValidation,
              }"
              @click="openFilePicker"
            >
              <img
                :src="profileImage"
                class="img-upload"
                alt="Profile Image"
                width="150"
                height="150"
              />
              <div class="hover-overlay">Click to Upload</div>
            </div>
            <input
              type="file"
              ref="fileInput"
              class="d-none"
              @change="handleImageUpload"
              accept="image/*"
            />
            <div v-if="fileErrors.profileImage" class="text-danger small mt-2">
              {{ fileErrors.profileImage }}
            </div>
            <div
              v-if="
                !uploadStatus.profileImage.uploaded &&
                showValidation &&
                !fileErrors.profileImage
              "
              class="text-danger small mt-2"
            >
              Profile picture is required
            </div>
            <div v-if="uploadStatus.profileImage.uploading" class="mt-2 small">
              <div class="spinner-border spinner-border-sm me-2"></div>
              <span>Uploading...</span>
            </div>
            <div v-if="uploadStatus.profileImage.uploaded" class="mt-2 small">
              <i class="bi bi-check-circle-fill text-success me-2"></i>
              <span class="text-success">Uploaded</span>
              <button
                class="btn btn-sm btn-outline-danger ms-2"
                @click.stop="deleteUploadedFile('profileImage')"
                type="button"
              >
                <i class="bi bi-trash"></i>
              </button>
            </div>
          </div>
          <div class="col-md-6 mb-3">
            <label for="exampleInput1" class="form-label"
              >Application Number <span class="text-danger">*</span>
            </label>
            <p
              class="form-control-plaintext border rounded px-2 bg-body-secondary"
            >
              {{ userData.applicationNumber }}
            </p>
          </div>
        </div>
        <div class="row">
          <div class="col-md-4">
            <div class="mb-3">
              <label for="exampleInput2" class="form-label"
                >Surname <span class="text-danger">*</span>
              </label>
              <p
                class="form-control-plaintext border rounded px-2 bg-body-secondary text-capitalize"
              >
                {{ userData.lastname }}
              </p>
            </div>
          </div>
          <div class="col-md-4">
            <div class="mb-3">
              <label for="exampleInput3" class="form-label"
                >First Name <span class="text-danger">*</span>
              </label>
              <p
                class="form-control-plaintext border rounded px-2 bg-body-secondary text-capitalize"
              >
                {{ userData.firstname }}
              </p>
            </div>
          </div>
          <div class="col-md-4">
            <div class="mb-3">
              <label for="exampleInput4" class="form-label">Other Name </label>
              <p
                class="form-control-plaintext border rounded px-2 bg-body-secondary text-capitalize"
              >
                {{ userData.othername }}
              </p>
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-md-4">
            <div class="mb-3">
              <label for="exampleInput5" class="form-label"
                >Gender <span class="text-danger">*</span>
              </label>
              <select
                class="form-select"
                :class="{ 'is-invalid': !formData.gender && formData.touched }"
                v-model="formData.gender"
                aria-label="Default select example"
              >
                <option disabled value="">-- Select --</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
              <div class="invalid-feedback">Gender is required</div>
            </div>
          </div>
          <div class="col-md-4">
            <div class="mb-3">
              <label for="exampleFormControlInput1" class="form-label"
                >Email address <span class="text-danger">*</span></label
              >
              <p
                class="form-control-plaintext border rounded px-2 bg-body-secondary"
              >
                {{ userData.email }}
              </p>
            </div>
          </div>
          <div class="col-md-4">
            <div class="mb-3">
              <label for="exampleFormControlInput2" class="form-label"
                >Mobile No <span class="text-danger">*</span></label
              >
              <p
                class="form-control-plaintext border rounded px-2 bg-body-secondary"
              >
                {{ userData.phone }}
              </p>
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-md-4">
            <div class="mb-3">
              <label for="dob" class="form-label"
                >Date of Birth <span class="text-danger">*</span>
              </label>
              <input
                type="date"
                class="form-control"
                :class="{ 'is-invalid': !formData.dob && formData.touched }"
                :max="maxDateOfBirth"
                v-model="formData.dob"
              />
              <div class="invalid-feedback">
                Date of birth is required (Must be at least 15 years old)
              </div>
            </div>
          </div>
          <div class="col-md-8">
            <div class="mb-3">
              <label for="perAddress" class="form-label"
                >Permanent Address <span class="text-danger">*</span></label
              >
              <input
                type="text"
                class="form-control"
                :class="{
                  'is-invalid': !formData.permanentAddress && formData.touched,
                }"
                id="perAddress"
                placeholder="123 ABC"
                v-model="formData.permanentAddress"
              />
              <div class="invalid-feedback">Permanent address is required</div>
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-md-4">
            <div class="mb-3">
              <label class="form-label">
                Nationality <span class="text-danger">*</span>
              </label>
              <v-select
                v-model="selectedNationality"
                :options="countries.map((c) => c.name)"
                placeholder="-- Select --"
                :disabled="isOLevelCompletionMode"
                :class="{
                  'is-invalid-select': !selectedNationality && showValidation,
                }"
                class="custom-select"
              />
              <div
                v-if="!selectedNationality && showValidation"
                class="invalid-feedback d-block"
              >
                Nationality is required
              </div>
            </div>
          </div>
          <div class="col-md-4">
            <div class="mb-3">
              <label class="form-label">
                State of Origin <span class="text-danger">*</span>
              </label>
              <v-select
                class="custom-select"
                :class="{
                  'is-invalid-select': !selectedState && showValidation,
                }"
                v-model="selectedState"
                :options="states"
                :reduce="(state) => state"
                placeholder="-- Select --"
                :disabled="
                  isOLevelCompletionMode ||
                  selectedNationality !== 'Nigeria' ||
                  !states.length
                "
                @update:modelValue="fetchLGAs"
              />
              <div
                v-if="!selectedState && showValidation"
                class="invalid-feedback d-block"
              >
                State of Origin is required
              </div>
            </div>
          </div>
          <div class="col-md-4">
            <div class="mb-3">
              <label class="form-label">
                LGA <span class="text-danger">*</span>
              </label>
              <v-select
                class="custom-select"
                :class="{ 'is-invalid-select': !selectedLGA && showValidation }"
                v-model="selectedLGA"
                :options="lgas"
                :reduce="(lga) => lga"
                placeholder="-- Select --"
                :disabled="
                  isOLevelCompletionMode ||
                  !selectedState ||
                  selectedNationality !== 'Nigeria' ||
                  !lgas.length
                "
              />
              <div
                v-if="!selectedLGA && showValidation"
                class="invalid-feedback d-block"
              >
                LGA is required
              </div>
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-md-4">
            <div class="mb-3">
              <label class="form-label">
                Place of Birth <span class="text-danger">*</span>
              </label>
              <v-select
                class="custom-select"
                :class="{ 'is-invalid-select': !selectedPOB && showValidation }"
                v-model="selectedPOB"
                :options="states"
                :reduce="(state) => state"
                placeholder="-- Select --"
                :disabled="
                  isOLevelCompletionMode ||
                  selectedNationality !== 'Nigeria' ||
                  !states.length
                "
              />
              <div
                v-if="!selectedPOB && showValidation"
                class="invalid-feedback d-block"
              >
                Place of Birth is required
              </div>
            </div>
          </div>
          <div class="col-md-4">
            <div class="mb-3">
              <label for="exampleInput2" class="form-label">
                Marital Status <span class="text-danger">*</span>
              </label>
              <select
                class="form-select"
                :class="{
                  'is-invalid': !formData.maritalStatus && showValidation,
                }"
                v-model="formData.maritalStatus"
                aria-label="Marital status select"
              >
                <option selected disabled value="">-- Select --</option>
                <option value="single">Single</option>
                <option value="married">Married</option>
                <option value="divorced">Divorced</option>
                <option value="widowed">Widowed</option>
              </select>
              <div class="invalid-feedback">Marital status is required</div>
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-md-4">
            <div class="mb-3">
              <label for="exampleInput2" class="form-label"
                >Parent/Guardian Name <span class="text-danger">*</span>
              </label>
              <input
                type="text"
                class="form-control"
                :class="{
                  'is-invalid': !formData.parentGuardian.name && showValidation,
                }"
                v-model="formData.parentGuardian.name"
              />
              <div class="invalid-feedback">
                Parent/Guardian name is required
              </div>
            </div>
          </div>
          <div class="col-md-4">
            <div class="mb-3">
              <label for="exampleInput2" class="form-label"
                >Parent/Guardian Address <span class="text-danger">*</span>
              </label>
              <input
                type="text"
                class="form-control"
                :class="{
                  'is-invalid':
                    !formData.parentGuardian.address && showValidation,
                }"
                v-model="formData.parentGuardian.address"
              />
              <div class="invalid-feedback">
                Parent/Guardian address is required
              </div>
            </div>
          </div>
          <div class="col-md-4">
            <div class="mb-3">
              <label for="exampleInput2" class="form-label"
                >Parent/Guardian Phone <span class="text-danger">*</span>
              </label>
              <input
                type="phone"
                class="form-control"
                :class="{
                  'is-invalid':
                    !formData.parentGuardian.phone && showValidation,
                }"
                v-model="formData.parentGuardian.phone"
              />
              <div class="invalid-feedback">
                Parent/Guardian phone is required
              </div>
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-md-4">
            <div class="mb-3">
              <label for="exampleInput2" class="form-label"
                >Next of Kin Name <span class="text-danger">*</span>
              </label>
              <input
                type="text"
                class="form-control"
                :class="{
                  'is-invalid': !formData.nextOfKin.name && showValidation,
                }"
                v-model="formData.nextOfKin.name"
              />
              <div class="invalid-feedback">Next of kin name is required</div>
            </div>
          </div>
          <div class="col-md-4">
            <div class="mb-3">
              <label for="kinRelationship" class="form-label">
                Relationship with Next of Kin <span class="text-danger">*</span>
              </label>
              <select
                v-model="kinRelationship"
                class="form-select"
                :class="{ 'is-invalid': !kinRelationship && showValidation }"
              >
                <option selected disabled value="">-- Select --</option>
                <option value="Parent">Parent</option>
                <option value="Sibling">Sibling</option>
                <option value="Spouse">Spouse</option>
                <option value="Child">Child</option>
                <option value="Grandparent">Grandparent</option>
                <option value="Uncle/Aunt">Uncle / Aunt</option>
                <option value="Cousin">Cousin</option>
                <option value="Nephew/Niece">Nephew / Niece</option>
                <option value="Guardian">Guardian</option>
                <option value="Friend">Friend</option>
                <option value="Other">Other (Specify)</option>
              </select>
              <div class="invalid-feedback">Relationship is required</div>
            </div>
          </div>

          <!-- Show Input Field If "Other" is Selected -->
          <div class="col-md-4" v-if="kinRelationship === 'Other'">
            <div class="mb-3">
              <label for="otherKinRelationship" class="form-label">
                Specify Relationship <span class="text-danger">*</span>
              </label>
              <input
                v-model="kinRelationship"
                type="text"
                class="form-control"
                placeholder="Enter relationship"
              />
            </div>
          </div>

          <div class="col-md-4">
            <div class="mb-3">
              <label for="exampleInput2" class="form-label"
                >Phone Number(Next of Kin) <span class="text-danger">*</span>
              </label>
              <input
                type="phone"
                class="form-control"
                :class="{
                  'is-invalid': !formData.nextOfKin.phone && showValidation,
                }"
                v-model="formData.nextOfKin.phone"
              />
              <div class="invalid-feedback">Next of kin phone is required</div>
            </div>
          </div>
        </div>
        <!-- Programme Information -->
        <div class="d-flex justify-content-center mt-5 mb-3">
          <div
            class="title-container d-inline-flex align-items-center position-relative"
          >
            <i class="bi bi-book fs-4 me-2"></i>
            <h6 class="fw-bold mb-0">Programme Information</h6>
          </div>
        </div>
        <div class="row">
          <div class="col-md-5">
            <div class="mb-3">
              <label for="exampleInput2" class="form-label"
                >Programme Type
              </label>
              <p
                class="form-control-plaintext border rounded px-2 bg-body-secondary"
              >
                {{ programmeTypeLabel }}
              </p>
            </div>
          </div>
          <div class="col-md-3">
            <div class="mb-3">
              <label for="exampleInput3" class="form-label"
                >Academic Session
              </label>
              <p
                class="form-control-plaintext border rounded px-2 bg-body-secondary"
              >
                {{ entrySessionLabel }}
              </p>
            </div>
          </div>
          <div class="col-md-4">
            <div class="mb-3">
              <label for="exampleInput4" class="form-label">School </label>
              <p
                class="form-control-plaintext border rounded px-2 bg-body-secondary"
              >
                {{ programmeSchoolName }}
              </p>
            </div>
          </div>
          <div class="col-md-6">
            <div class="mb-3">
              <label for="department" class="form-label">Department</label>
              <p
                class="form-control-plaintext border rounded px-2 bg-body-secondary text-capitalize"
              >
                {{ programmeDepartmentName }}
              </p>
            </div>
          </div>
          <div class="col-md-6">
            <div class="mb-3">
              <label for="program" class="form-label">Program</label>
              <p
                class="form-control-plaintext border rounded px-2 bg-body-secondary text-capitalize"
              >
                {{ programmeName }}
              </p>
            </div>
          </div>
        </div>
        <!-- Academic Background -->
        <div class="d-flex justify-content-center mt-5 mb-3">
          <div
            class="title-container d-inline-flex align-items-center position-relative"
          >
            <i class="bi bi-mortarboard-fill fs-4 me-2"></i>
            <h6 class="fw-bold mb-0">Academic Background</h6>
          </div>
        </div>
        <div class="row">
          <div class="col-md-6">
            <div class="mb-3">
              <label for="exampleInput2" class="form-label"
                >Primary School <span class="text-danger">*</span>
              </label>
              <input
                type="text"
                class="form-control"
                :class="{
                  'is-invalid':
                    !formData.academicBackground.primary.name && showValidation,
                }"
                id="exampleInput2"
                v-model="formData.academicBackground.primary.name"
              />
              <div class="invalid-feedback">
                Primary School name is required
              </div>
            </div>
          </div>
          <div class="col-md-3">
            <div class="mb-3">
              <label for="exampleInput3" class="form-label"
                >Start date <span class="text-danger">*</span>
              </label>
              <input
                type="date"
                class="form-control"
                :class="{
                  'is-invalid':
                    !formData.academicBackground.primary.startDate &&
                    showValidation,
                }"
                id="exampleInput3"
                v-model="formData.academicBackground.primary.startDate"
                :min="minPrimaryStartDate"
                :max="maxPrimaryStartDate"
                :disabled="!formData.dob"
              />
              <div class="invalid-feedback">
                Primary School start date is required (at least 5 years after
                birth)
              </div>
              <small v-if="!formData.dob" class="text-muted"
                >Select date of birth first</small
              >
            </div>
          </div>
          <div class="col-md-3">
            <div class="mb-3">
              <label for="exampleInput4" class="form-label"
                >End Date <span class="text-danger">*</span></label
              >
              <input
                type="date"
                class="form-control"
                :class="{
                  'is-invalid':
                    !formData.academicBackground.primary.endDate &&
                    showValidation,
                }"
                id="exampleInput4"
                v-model="formData.academicBackground.primary.endDate"
                :min="minPrimaryEndDate"
                :max="maxPrimaryEndDate"
                :disabled="!formData.academicBackground.primary.startDate"
              />
              <div class="invalid-feedback">
                Primary School end date is required (must be after start date)
              </div>
              <small
                v-if="!formData.academicBackground.primary.startDate"
                class="text-muted"
                >Select start date first</small
              >
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-md-6">
            <div class="mb-3">
              <label for="exampleInput2" class="form-label"
                >Secondary School <span class="text-danger">*</span>
              </label>
              <input
                type="text"
                class="form-control"
                :class="{
                  'is-invalid':
                    !formData.academicBackground.secondary.name &&
                    showValidation,
                }"
                id="exampleInput2"
                v-model="formData.academicBackground.secondary.name"
              />
              <div class="invalid-feedback">
                Secondary School name is required
              </div>
            </div>
          </div>
          <div class="col-md-3">
            <div class="mb-3">
              <label for="exampleInput3" class="form-label"
                >Start date <span class="text-danger">*</span>
              </label>
              <input
                type="date"
                class="form-control"
                :class="{
                  'is-invalid':
                    !formData.academicBackground.secondary.startDate &&
                    showValidation,
                }"
                id="exampleInput3"
                v-model="formData.academicBackground.secondary.startDate"
                :min="minSecondaryStartDate"
                :max="maxSecondaryStartDate"
                :disabled="!formData.academicBackground.primary.endDate"
              />
              <div class="invalid-feedback">
                Secondary School start date is required (must be after primary
                school)
              </div>
              <small
                v-if="!formData.academicBackground.primary.endDate"
                class="text-muted"
                >Select primary school dates first</small
              >
            </div>
          </div>
          <div class="col-md-3">
            <div class="mb-3">
              <label for="exampleInput4" class="form-label"
                >End Date <span class="text-danger">*</span></label
              >
              <input
                type="date"
                class="form-control"
                :class="{
                  'is-invalid':
                    !formData.academicBackground.secondary.endDate &&
                    showValidation,
                }"
                id="exampleInput4"
                v-model="formData.academicBackground.secondary.endDate"
                :min="minSecondaryEndDate"
                :max="maxSecondaryEndDate"
                :disabled="!formData.academicBackground.secondary.startDate"
              />
              <div class="invalid-feedback">
                Secondary School end date is required (must be after start date)
              </div>
              <small
                v-if="!formData.academicBackground.secondary.startDate"
                class="text-muted"
                >Select start date first</small
              >
            </div>
          </div>
        </div>
      </fieldset>

      <div v-if="showAwaitingResultToggle" class="form-check mt-3">
        <input
          class="form-check-input"
          type="checkbox"
          id="awaitingResultCheck"
          v-model="oLevelResultStatus"
          :true-value="O_LEVEL_RESULT_STATUS.AWAITING"
          :false-value="O_LEVEL_RESULT_STATUS.PROVIDED"
        />
        <label class="form-check-label" for="awaitingResultCheck">
          Tick if awaiting results. Result can be uploaded later.
        </label>
      </div>
      <div v-else class="alert alert-info mt-3 mb-0">
        Only Post Secondary School, O'Level Result, and O'Level uploads are
        editable here. Other application fields are locked because the
        application has already been submitted.
      </div>
      <div v-if="isAwaitingOLevelResult" class="alert alert-warning mt-3 mb-0">
        Post Secondary School, O'Level Result, and O'Level result uploads are
        now optional for this application. You can provide them later in the
        student portal Resources page.
      </div>

      <!-- Post Secondary School -->
      <div class="d-flex justify-content-center mt-3 mb-3">
        <div
          class="title-container d-inline-flex align-items-center position-relative"
        >
          <i class="bi bi-person-circle fs-4 me-2"></i>
          <h6 class="fw-bold mb-0">
            Post Secondary School
            <span class="fw-normal" style="font-size: small"
              >(e.g. WASSSCE, NECO, GCE, etc)</span
            >
          </h6>
        </div>
      </div>

      <div
        class="row"
        :class="{ 'section-disabled': isAwaitingOLevelResult }"
        v-for="(sitting, index) in sittings"
        :key="index"
      >
        <h6 class="">Sitting {{ index + 1 }}</h6>

        <div class="col-md-4">
          <label class="form-label"
            >Exam Type <span class="text-danger">*</span></label
          >
          <select
            class="form-control"
            :class="{
              'is-invalid':
                !isAwaitingOLevelResult && !sitting.examType && showValidation,
            }"
            v-model="sitting.examType"
            :disabled="isAwaitingOLevelResult"
          >
            <option value="" disabled>-- Select --</option>
            <option value="SSCE">SSCE</option>
            <option value="NECO">NECO</option>
            <option value="GCE">GCE</option>
            <option value="NABTEB">NABTEB</option>
          </select>
          <div class="invalid-feedback">Exam Type is required</div>
        </div>
        <div class="col-md-4">
          <label class="form-label"
            >Exam Year <span class="text-danger">*</span></label
          >
          <select
            class="form-control"
            :class="{
              'is-invalid':
                !isAwaitingOLevelResult && !sitting.examYear && showValidation,
            }"
            v-model="sitting.examYear"
            :disabled="isAwaitingOLevelResult"
          >
            <option value="" disabled>-- Select --</option>
            <option v-for="year in examYears" :key="year" :value="year">
              {{ year }}
            </option>
          </select>
          <div class="invalid-feedback">Exam Year is required</div>
        </div>
        <div class="col-md-4">
          <label class="form-label"
            >Exam Number <span class="text-danger">*</span></label
          >
          <input
            type="text"
            class="form-control"
            :class="{
              'is-invalid':
                !isAwaitingOLevelResult &&
                !sitting.examNumber &&
                showValidation,
            }"
            v-model="sitting.examNumber"
            placeholder="Enter Exam Number"
            :disabled="isAwaitingOLevelResult"
          />
          <div class="invalid-feedback">Exam Number is required</div>
        </div>
        <a
          @click="removeSitting(index)"
          v-if="sittings.length > 1 && !isAwaitingOLevelResult"
          class="link-danger link-offset-2 link-underline-opacity-25 link-underline-opacity-100-hover mt-2 text-end"
          style="text-decoration-style: dashed"
        >
          Remove
        </a>
      </div>

      <button
        type="button"
        class="btn btn-outline-secondary btn-sm mt-2"
        @click="addSitting"
        :disabled="sittings.length >= 2 || isAwaitingOLevelResult"
      >
        <i class="bi bi-plus"></i> Add
      </button>

      <!-- Result -->
      <div class="d-flex justify-content-center mt-5 mb-3">
        <div
          class="title-container d-inline-flex align-items-center position-relative"
        >
          <i class="bi bi-postcard fs-4 me-2"></i>
          <h6 class="fw-bold mb-0">O'Level Result</h6>
        </div>
      </div>

      <div
        v-for="(subject, index) in subjects"
        :key="index"
        class="row mb-3"
        :class="{ 'section-disabled': isAwaitingOLevelResult }"
      >
        <div class="col-md-5">
          <label class="form-label"
            >Subject <span class="text-danger">*</span></label
          >
          <select
            class="form-control"
            :class="{
              'is-invalid':
                !isAwaitingOLevelResult && !subject.name && showValidation,
            }"
            v-model="subject.name"
            :disabled="index < 2 || isAwaitingOLevelResult"
          >
            <option value="" disabled>-- Select --</option>
            <option
              v-for="option in availableSubjects(index)"
              :key="option"
              :value="option"
            >
              {{ option }}
            </option>
          </select>
          <div class="invalid-feedback">Subject is required</div>
        </div>
        <div class="col-md-3">
          <label class="form-label">
            Grade <span class="text-danger">*</span></label
          >
          <select
            class="form-control"
            :class="{
              'is-invalid':
                !isAwaitingOLevelResult && !subject.grade && showValidation,
            }"
            v-model="subject.grade"
            :disabled="isAwaitingOLevelResult"
          >
            <option value="" disabled>-- Select --</option>
            <option v-for="grade in grades" :key="grade" :value="grade">
              {{ grade }}
            </option>
          </select>
          <div class="invalid-feedback">Grade is required</div>
        </div>
        <div class="col-md-3">
          <label class="form-label">
            Sitting <span class="text-danger">*</span></label
          >
          <select
            class="form-select"
            :class="{
              'is-invalid':
                !isAwaitingOLevelResult && !subject.sitting && showValidation,
            }"
            v-model="subject.sitting"
            aria-label="Default select example"
            :disabled="isAwaitingOLevelResult"
          >
            <option value="" disabled>-- Select --</option>
            <option
              v-for="(sitting, sitIndex) in sittings"
              :key="sitIndex"
              :value="sitIndex + 1"
            >
              Sitting {{ sitIndex + 1 }}
            </option>
          </select>
          <div class="invalid-feedback">Sitting is required</div>
        </div>
        <div class="col-md-1 d-flex align-items-end">
          <a
            @click="removeSubject(index)"
            v-if="index >= 2 && !isAwaitingOLevelResult"
            class="link-danger link-offset-2 link-underline-opacity-25 link-underline-opacity-100-hover"
            style="text-decoration-style: dashed"
          >
            Remove
          </a>
        </div>
      </div>

      <button
        class="btn btn-outline-secondary btn-sm mt-3"
        type="button"
        @click="addSubject"
        :disabled="subjects.length >= 11 || isAwaitingOLevelResult"
      >
        <i class="bi bi-plus"></i> Add Subject
      </button>

      <!-- Files Upload -->
      <div class="d-flex justify-content-center mt-5 mb-3">
        <div
          class="title-container d-inline-flex align-items-center position-relative"
        >
          <i class="bi bi-cloud-upload fs-4 me-2"></i>
          <h6 class="fw-bold mb-0">Files Upload</h6>
        </div>
      </div>

      <!-- Upload Progress Bar -->
      <div class="card mb-4">
        <div class="card-body">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <h6 class="mb-0">Storage Used</h6>
            <span
              class="badge"
              :class="
                totalSize > MAX_TOTAL_SIZE * 0.8 ? 'bg-warning' : 'bg-success'
              "
            >
              {{ totalSizeFormatted }}MB / 5MB
            </span>
          </div>
          <div class="progress">
            <div
              class="progress-bar"
              :class="
                totalSize > MAX_TOTAL_SIZE * 0.8 ? 'bg-warning' : 'bg-success'
              "
              :style="{ width: `${(totalSize / MAX_TOTAL_SIZE) * 100}%` }"
            ></div>
          </div>
          <small class="text-muted"
            >{{ remainingSizeFormatted }}MB remaining</small
          >
        </div>
      </div>

      <!-- O-Level Results Upload -->
      <div
        class="card mb-3"
        :class="{
          'border-danger':
            !isAwaitingOLevelResult &&
            !allOLevelResultsUploaded &&
            showValidation,
          'section-disabled': isAwaitingOLevelResult,
        }"
      >
        <div class="card-body">
          <h6 class="card-title">
            O-Level Results
            <span v-if="!isAwaitingOLevelResult" class="text-danger">*</span>
          </h6>
          <p v-if="!isAwaitingOLevelResult" class="text-muted small">
            Upload PDF files for each sitting. Max size: 2MB per file
          </p>
          <p v-else class="text-muted small mb-0">
            O-Level result uploads are disabled while awaiting result is
            selected.
          </p>

          <div
            v-if="!isAwaitingOLevelResult && sittingNumbers.length === 0"
            class="alert alert-info"
          >
            Please add examination sittings in the Post Secondary School section
            first.
          </div>

          <div
            v-for="num in sittingNumbers"
            :key="`sitting${num}`"
            class="mb-3 p-3 border rounded"
            :class="{
              'border-danger':
                !isAwaitingOLevelResult &&
                !uploadStatus.oLevelResults[`sitting${num}`]?.uploaded &&
                showValidation,
            }"
          >
            <h6 class="mb-2">Sitting {{ num }}</h6>
            <input
              type="file"
              class="form-control"
              :class="{
                'is-invalid':
                  (!isAwaitingOLevelResult && fileErrors[`olevel${num}`]) ||
                  (!isAwaitingOLevelResult &&
                    !uploadStatus.oLevelResults[`sitting${num}`]?.uploaded &&
                    showValidation),
              }"
              accept="application/pdf"
              @change="handlePdfSelect('olevel', $event, num)"
              :disabled="
                isAwaitingOLevelResult ||
                uploadStatus.oLevelResults[`sitting${num}`]?.uploading ||
                uploadStatus.oLevelResults[`sitting${num}`]?.uploaded
              "
            />
            <div
              v-if="fileErrors[`olevel${num}`]"
              class="invalid-feedback d-block"
            >
              {{ fileErrors[`olevel${num}`] }}
            </div>
            <div
              v-if="
                !uploadStatus.oLevelResults[`sitting${num}`]?.uploaded &&
                showValidation &&
                !isAwaitingOLevelResult &&
                !fileErrors[`olevel${num}`]
              "
              class="invalid-feedback d-block"
            >
              O-Level Result for Sitting {{ num }} is required
            </div>
            <div
              v-if="uploadStatus.oLevelResults[`sitting${num}`]?.uploading"
              class="mt-2"
            >
              <div class="spinner-border spinner-border-sm me-2"></div>
              <span>Uploading...</span>
            </div>
            <div
              v-if="uploadStatus.oLevelResults[`sitting${num}`]?.uploaded"
              class="mt-2"
            >
              <i class="bi bi-check-circle-fill text-success me-2"></i>
              <span class="text-success">Uploaded successfully</span>
              <button
                class="btn btn-sm btn-outline-danger ms-3"
                @click="deleteUploadedFile('olevel', num)"
                type="button"
              >
                <i class="bi bi-trash"></i> Remove
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- JAMB Result Upload -->
      <div
        class="card mb-3"
        :class="{
          'border-danger':
            !isOLevelCompletionMode &&
            !uploadStatus.jambResult.uploaded &&
            showValidation,
          'section-disabled': isOLevelCompletionMode,
        }"
      >
        <div class="card-body">
          <h6 class="card-title">
            JAMB Result <span class="text-danger">*</span>
          </h6>
          <p class="text-muted small">Upload PDF file. Max size: 2MB</p>

          <input
            type="file"
            class="form-control"
            :class="{
              'is-invalid':
                (!isOLevelCompletionMode && fileErrors.jambResult) ||
                (!isOLevelCompletionMode &&
                  !uploadStatus.jambResult.uploaded &&
                  showValidation),
            }"
            accept="application/pdf"
            @change="handlePdfSelect('jambResult', $event)"
            :disabled="
              isOLevelCompletionMode ||
              uploadStatus.jambResult.uploading ||
              uploadStatus.jambResult.uploaded
            "
          />
          <div v-if="fileErrors.jambResult" class="invalid-feedback d-block">
            {{ fileErrors.jambResult }}
          </div>
          <div
            v-if="
              !uploadStatus.jambResult.uploaded &&
              showValidation &&
              !isOLevelCompletionMode &&
              !fileErrors.jambResult
            "
            class="invalid-feedback d-block"
          >
            JAMB Result is required
          </div>
          <div v-if="uploadStatus.jambResult.uploading" class="mt-2">
            <div class="spinner-border spinner-border-sm me-2"></div>
            <span>Uploading...</span>
          </div>
          <div v-if="uploadStatus.jambResult.uploaded" class="mt-2">
            <i class="bi bi-check-circle-fill text-success me-2"></i>
            <span class="text-success">Uploaded successfully</span>
            <button
              class="btn btn-sm btn-outline-danger ms-3"
              @click="deleteUploadedFile('jambResult')"
              type="button"
              :disabled="isOLevelCompletionMode"
            >
              <i class="bi bi-trash"></i> Remove
            </button>
          </div>
        </div>
      </div>

      <!-- Birth Certificate Upload -->
      <div
        class="card mb-3"
        :class="{
          'border-danger':
            !isOLevelCompletionMode &&
            !uploadStatus.birthCertificate.uploaded &&
            showValidation,
          'section-disabled': isOLevelCompletionMode,
        }"
      >
        <div class="card-body">
          <h6 class="card-title">
            Birth Certificate <span class="text-danger">*</span>
          </h6>
          <p class="text-muted small">Upload PDF file. Max size: 2MB</p>

          <input
            type="file"
            class="form-control"
            :class="{
              'is-invalid':
                (!isOLevelCompletionMode && fileErrors.birthCertificate) ||
                (!isOLevelCompletionMode &&
                  !uploadStatus.birthCertificate.uploaded &&
                  showValidation),
            }"
            accept="application/pdf"
            @change="handlePdfSelect('birthCertificate', $event)"
            :disabled="
              isOLevelCompletionMode ||
              uploadStatus.birthCertificate.uploading ||
              uploadStatus.birthCertificate.uploaded
            "
          />
          <div
            v-if="fileErrors.birthCertificate"
            class="invalid-feedback d-block"
          >
            {{ fileErrors.birthCertificate }}
          </div>
          <div
            v-if="
              !uploadStatus.birthCertificate.uploaded &&
              showValidation &&
              !isOLevelCompletionMode &&
              !fileErrors.birthCertificate
            "
            class="invalid-feedback d-block"
          >
            Birth Certificate is required
          </div>
          <div v-if="uploadStatus.birthCertificate.uploading" class="mt-2">
            <div class="spinner-border spinner-border-sm me-2"></div>
            <span>Uploading...</span>
          </div>
          <div v-if="uploadStatus.birthCertificate.uploaded" class="mt-2">
            <i class="bi bi-check-circle-fill text-success me-2"></i>
            <span class="text-success">Uploaded successfully</span>
            <button
              class="btn btn-sm btn-outline-danger ms-3"
              @click="deleteUploadedFile('birthCertificate')"
              type="button"
              :disabled="isOLevelCompletionMode"
            >
              <i class="bi bi-trash"></i> Remove
            </button>
          </div>
        </div>
      </div>

      <!-- Upload Summary -->
      <div v-if="allFilesUploaded" class="alert alert-success">
        <i class="bi bi-check-circle-fill me-2"></i>
        All required files have been uploaded successfully!
      </div>

      <!-- Submit Button -->
      <div class="text-center mt-4">
        <button
          type="button"
          class="btn btn-outline-primary me-3"
          @click="saveDraft"
          :disabled="isSaving"
        >
          <span
            v-if="isSaving"
            class="spinner-border spinner-border-sm me-2"
            role="status"
            aria-hidden="true"
          ></span>
          {{ isSaving ? "Saving..." : "Save Progress" }}
        </button>
        <button
          type="submit"
          class="btn btn-success"
          :disabled="formData.isSubmitting"
        >
          <span
            v-if="formData.isSubmitting"
            class="spinner-border spinner-border-sm me-2"
            role="status"
            aria-hidden="true"
          ></span>
          {{ submitButtonText }}
        </button>
        <div v-if="draftSaved" class="mt-2 text-success small">
          <i class="bi bi-check-circle-fill me-1"></i> Progress saved
          automatically
        </div>
        <div v-if="draftSaveError" class="mt-2 text-danger small">
          <i class="bi bi-exclamation-circle-fill me-1"></i>
          {{ draftSaveError }}
        </div>
      </div>
    </form>
  </div>
</template>

<style scoped>
.border-custom-secondary {
  border-color: #e9b500 !important;
}

.title-container::after {
  content: "";
  position: absolute;
  bottom: -5px;
  /* Position below the text */
  left: 50%;
  width: 20%;
  /* Border width */
  height: 2px;
  /* Border thickness */
  background-color: #e9b500;
  /* Border color */
  transform: translateX(-50%);
  /* Center the border */
}

.image-upload-container {
  position: relative;
  width: 150px;
  height: 150px;
  cursor: pointer;
  border-radius: 8px;
  overflow: hidden;
  border: 2px dashed #ccc;
}

.img-upload {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 8px;
  padding: 5px;
}

.hover-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: bold;
  opacity: 0;
  transition: opacity 0.3s ease-in-out;
  border-radius: 8px;
}

.image-upload-container:hover .hover-overlay {
  opacity: 1;
}

.custom-select {
  display: block;
  width: 100%;
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.5;
  color: var(--bs-body-color);
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  background-color: var(--bs-body-bg);
  border-radius: var(--bs-border-radius);
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.is-invalid-select :deep(.vs__dropdown-toggle) {
  border-color: #dc3545;
}

.is-invalid-select :deep(.vs__dropdown-toggle):focus {
  border-color: #dc3545;
  box-shadow: 0 0 0 0.25rem rgba(220, 53, 69, 0.25);
}

.card-title {
  font-size: 1rem;
  font-weight: 600;
}

.progress {
  height: 25px;
}

.locked-form-section {
  border: 0;
  margin: 0;
  padding: 0;
  opacity: 0.72;
}

.section-disabled {
  opacity: 0.72;
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
}
</style>
